import React, { useState, useEffect } from 'react';
import { FiHeart, FiMessageCircle } from 'react-icons/fi';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import Chart from './Chart';
import { Widget, WidgetHeader, WidgetTitle, WidgetDescription, WidgetBody, WidgetKpi, WidgetKpiValue, WidgetWrapper, WidgetWrapperCol } from '@/components/custom/widget';
import TopWidget from './TopWidget';
import HitWidget from './HitWidget';
import IndividualWidget from './IndividualWidget';
import { Loader } from '@/components/custom/loader';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { DashboardContent } from '@/layouts/dashboard';

const DashboardAdmin = () => {
  const { t } = useTranslation();
  const client = useClient();
  const [rangeDates, setRangeDates] = useState([null, null]);
  const [dashboardData, setDashboardData] = useState();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [rangeDates]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchData = async () => {
    try {
      let filterParams = '';
      if (rangeDates && rangeDates.filter(a => a).length == 2) {
        const [initialDate, endDate] = rangeDates.map(d => d.toISOString().split('T')[0]);
        filterParams = `?initial_date=${initialDate}&end_date=${endDate}`;
      }

      const { data } = await client.fetch(`admin${filterParams}`);
      setDashboardData(data);
      setIsLoading(false);
    } catch (e) {
      toast.error(t('toast.error'));
      console.error(e);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await client.fetch(`users?simple=true`);
      setUsers(data);
    } catch (e) {
      toast.error(t('toast.error'));
      console.error(e);
    }
  };

  return (
    <>
      {isLoading ? (
        <DashboardContent variant="plain">
          <Loader />
        </DashboardContent>
      ) : (
        <DashboardContent variant="plain">
          <div className="filter-bar">
            <div className="filter-bar__row">
              <div className="filter-bar__label">Selecione um Período</div>
              <DateRangePicker
                onChange={setRangeDates}
                value={rangeDates}
              />
            </div>

            <button
              className="btn btn--primary btn--wide"
              onClick={() => setRangeDates([null, null])}
            >
              Limpar filtro
            </button>
          </div>

          <WidgetWrapper>
            <WidgetKpi title="Índice de Acerto">
              <WidgetKpiValue>{dashboardData?.hitPercentage}%</WidgetKpiValue>
            </WidgetKpi>

            <WidgetKpi title="Estudantes Totais">
              <WidgetKpiValue>{dashboardData?.totalUsers}</WidgetKpiValue>
            </WidgetKpi>

            <WidgetKpi title="Nº Total Questões Respondidas">
              <WidgetKpiValue>{dashboardData?.totalAnswers}</WidgetKpiValue>
            </WidgetKpi>

            <WidgetKpi title="Dados de Engajamento">
              <WidgetKpiValue>
                <FiHeart /> {dashboardData?.commentsCount}
              </WidgetKpiValue>
              <WidgetKpiValue>
                <FiMessageCircle /> {dashboardData?.likesCount}
              </WidgetKpiValue>
            </WidgetKpi>
          </WidgetWrapper>

          <WidgetWrapper variant="chart">
            <Widget>
              <WidgetHeader>
                <WidgetTitle>Dados de Engajamento</WidgetTitle>
              </WidgetHeader>
              <WidgetBody>
                {dashboardData && (
                  <Chart
                    name="Engajamento"
                    color="#442B8C"
                    chartData={dashboardData?.usersGrowth}
                  />
                )}
              </WidgetBody>
            </Widget>

            <Widget>
              <WidgetHeader>
                <WidgetTitle>Inscritos no OPEN</WidgetTitle>
              </WidgetHeader>
              <WidgetBody>
                {dashboardData && (
                  <Chart
                    name="Inscritos"
                    color="#32C9D3"
                    chartData={dashboardData?.subscriptionsGrowth}
                  />
                )}
              </WidgetBody>
            </Widget>

            <Widget>
              <WidgetHeader>
                <WidgetTitle>Cadernos de questões</WidgetTitle>
              </WidgetHeader>
              <WidgetBody>
                {dashboardData && (
                  <Chart
                    name="Cadernos"
                    color="#FDB462"
                    chartData={dashboardData?.questionBooksGrowth}
                  />
                )}
              </WidgetBody>
            </Widget>
          </WidgetWrapper>

          <WidgetWrapper variant="col">
            <WidgetWrapperCol>
              <Widget>
                <WidgetHeader>
                  <WidgetTitle>Aproveitamento Individual</WidgetTitle>
                  <WidgetDescription>Rendimento comparativo dos estudantes</WidgetDescription>
                </WidgetHeader>
                <WidgetBody>{users && users.length > 0 && <IndividualWidget users={users} />}</WidgetBody>
              </Widget>

              <Widget>
                <WidgetHeader>
                  <WidgetTitle>Acertos por Disciplina</WidgetTitle>
                  <WidgetDescription>Número de questões acertadas por matéria</WidgetDescription>
                </WidgetHeader>
                <WidgetBody>{dashboardData && <HitWidget hitsByTaxonomy={dashboardData?.hitsByTaxonomy} />}</WidgetBody>
              </Widget>
            </WidgetWrapperCol>

            <WidgetWrapperCol>
              <Widget>
                <WidgetHeader>
                  <WidgetTitle>Top 50 Estudantes</WidgetTitle>
                  <WidgetDescription>Rendimento individual dos estudantes</WidgetDescription>
                </WidgetHeader>
                <WidgetBody>{dashboardData && <TopWidget topUsers={dashboardData?.topUsers} />}</WidgetBody>
              </Widget>
            </WidgetWrapperCol>
          </WidgetWrapper>
        </DashboardContent>
      )}
    </>
  );
};

export default DashboardAdmin;
